import type { AisleCode, ShoppingLine } from '../../lib/api'

/**
 * Les rayons, dans l'ordre d'un trajet de magasin.
 *
 * Cet ordre est le seul intérêt du groupement : ranger la liste par rayon ne sert que si les
 * rayons eux-mêmes se suivent comme on les traverse. L'ordre alphabétique ferait repartir de
 * l'autre bout du magasin entre « boissons » et « boucherie ».
 */
export const RAYONS: readonly { readonly code: AisleCode; readonly nom: string }[] = [
  { code: 'fruits_legumes', nom: 'Fruits et légumes' },
  { code: 'boucherie', nom: 'Boucherie, poissonnerie' },
  { code: 'cremerie', nom: 'Crémerie' },
  { code: 'boulangerie', nom: 'Boulangerie' },
  { code: 'epicerie_salee', nom: 'Épicerie salée' },
  { code: 'epicerie_sucree', nom: 'Épicerie sucrée' },
  { code: 'surgeles', nom: 'Surgelés' },
  { code: 'boissons', nom: 'Boissons' },
  { code: 'hygiene', nom: 'Hygiène, beauté' },
  { code: 'entretien', nom: 'Entretien, maison' },
  { code: 'bebe_animaux', nom: 'Bébé, animaux' },
]

/** Le nom lisible d'un rayon. */
export function nomDuRayon(code: AisleCode | null): string {
  return RAYONS.find((rayon) => rayon.code === code)?.nom ?? 'Non classé'
}

export type GroupeDeRayon = {
  readonly code: AisleCode | null
  readonly nom: string
  readonly lignes: readonly ShoppingLine[]
}

/**
 * Groupe les articles par rayon, dans l'ordre du magasin.
 *
 * Les rayons vides ne sont pas rendus : une liste de courses de six articles n'a pas à montrer
 * onze titres. Les non classés ferment la marche — ce sont ceux qu'on finit par chercher, et les
 * mettre en tête ferait passer le reste après.
 */
export function grouperParRayon(lignes: readonly ShoppingLine[]): readonly GroupeDeRayon[] {
  const groupes: GroupeDeRayon[] = []

  for (const rayon of RAYONS) {
    const dedans = lignes.filter((ligne) => ligne.aisle === rayon.code)

    if (dedans.length > 0) {
      groupes.push({ code: rayon.code, nom: rayon.nom, lignes: dedans })
    }
  }

  const sansRayon = lignes.filter((ligne) => ligne.aisle === null)

  if (sansRayon.length > 0) {
    groupes.push({ code: null, nom: 'Non classé', lignes: sansRayon })
  }

  return groupes
}
